import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '@features/auth/auth.service';
import { TimeService } from '@services/common/time.service';
import { CollectionModel, ItemModel } from 'collection';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ChecklistStatusEnum } from './image.model';

@Component({
  selector: 'images-slider-tile',
  templateUrl: './images-slider-tile.component.html',
  styleUrls: ['./images-slider-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImagesSliderTileComponent implements OnInit, OnDestroy {
  collection: CollectionModel;
  currentIndex: number = 0;
  isDeleteShown: boolean | null = null;
  item: ItemModel;
  imageTypes = ['jpeg', 'jpg', 'png', 'bmp', 'gif', 'heic', 'heif'];
  unsupportedImageTypes = ['tif', 'tiff'];

  onDeleteSubject = new Subject();
  private _destroyed$ = new Subject();

  get getTimeFormat(): string {
    // @ts-ignore
    return this._timeService.getDateFormat(this.collection.items[this.currentIndex].created_at, 'DD.MM.YYYY');
  }

  prev(): boolean {
    if (this.currentIndex === 0) {
      return false;
    }

    this.currentIndex = this.currentIndex - 1;

    return true;
  }

  next(): boolean {
    if (this.isNextPageLoadNeeded && !this.item['documentsView']) {
      this.onScroll();
    }

    if (
      (this.item && this.currentIndex === this.collection.meta.totalCount) ||
      this.currentIndex === this.collection.items.length
    ) {
      return false;
    }

    this.currentIndex = this.currentIndex + 1;

    return true;
  }

  selectImage(index: number): void {
    this.currentIndex = index;
  }

  close(): void {
    this.dialogRef.close();
  }

  deleteAction(): void {
    if (!this.item) {
      this.data.subject.next({
        delete: true,
        modal: this.dialogRef,
        index: this.currentIndex
      });

      return;
    }

    const currentImage = this.collection.items[this.currentIndex];

    this.data.subject.next({
      delete: true,
      modal: this.dialogRef,
      image: currentImage,
      index: this.currentIndex,
      onDeleteSubject: this.onDeleteSubject
    });
  }

  onScroll(): void {
    if (!this.collection && !this.collection.loadByScrolling) {
      return;
    }

    if (this.collection.meta.currentPage === this.collection.meta.pageCount) {
      return;
    }

    if (!this.item) {
      return;
    }

    if (!this.collection.loading && this.collection.loaded && this.collection.meta.totalCount >= 15) {
      const page = this.collection.meta.currentPage + 1;
      this.collection.setParams('page', page).search();
    }
  }

  private get isNextPageLoadNeeded(): boolean {
    return (
      this.currentIndex === this.collection.items.length - 2 && this.collection.meta.currentPage < this.collection.meta.pageCount
    );
  }

  private isUserCanInteract(): void {
    const isOwner: boolean = Number(this.item.data.user_id) === Number(this._auth.currentUser.id);
    const isAssignedTo = Number(this.item.data.assigned_user_id) === Number(this._auth.currentUser.id);

    if (this.isDeleteShown === null) {
      this.isDeleteShown =
        (this._auth.isAdmin() || isOwner || isAssignedTo) && this.item.data.status.toLowerCase() !== ChecklistStatusEnum.Closed;
    }
  }

  ngOnInit(): void {
    if (this.item) {
      this.isUserCanInteract();
    } else {
      this.isDeleteShown = true;
    }

    this.onDeleteSubject
      .pipe(
        takeUntil(this._destroyed$),
        map(() => {
          const filteredCollection = this.collection.items;
          filteredCollection.splice(this.currentIndex, 1);

          if (!filteredCollection.length) {
            this.close();
          }

          if (this.currentIndex !== 0) {
            this.currentIndex--;
          }

          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  constructor(
    public dialogRef: MatDialogRef<any>,
    private _auth: AuthService,
    private _cdr: ChangeDetectorRef,
    private _timeService: TimeService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.collection = this.data.collection;
    this.currentIndex = this.data.index;
    this.item = this.data.item;
    this.isDeleteShown = this.data.isDeleteShown;
  }
}
